import { useEffect } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  width?: "sm" | "md" | "lg";
  align?: "center" | "right";
}

const WIDTHS = { sm: "max-w-md", md: "max-w-xl", lg: "max-w-3xl" };

export function Modal({ open, onClose, children, width = "md", align = "center" }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className={clsx(
        "fixed inset-0 z-50 flex bg-black/30 backdrop-blur-[2px] animate-fade-in",
        align === "center"
          ? "items-center justify-center p-4"
          : // Full-bleed on mobile (no backdrop margin to tap, so the in-panel Close button is
            // the only way out) — it must clear the notch/status bar/home-indicator, which a
            // portal rendered straight to document.body doesn't inherit from the app shell.
            "items-stretch justify-end pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)] pt-[env(safe-area-inset-top)]",
      )}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="presentation"
    >
      <div
        className={clsx(
          "bg-surface dark:bg-surface-dark text-ink dark:text-ink-dark shadow-popover flex flex-col",
          align === "center"
            ? clsx("w-full rounded-2xl animate-scale-in max-h-[90vh]", WIDTHS[width])
            : "h-full w-full sm:max-w-md animate-fade-in",
        )}
        role="dialog"
        aria-modal="true"
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
